import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Lock,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  CreditCard,
} from "lucide-react";
import Logo from "../components/Logo";
import usePageMeta from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [notFound, setNotFound] = useState(false);

  usePageMeta({
    title: "Paiement sécurisé · ZF Industries",
    description: "Page de paiement sécurisée par Stripe pour votre devis ZF Industries.",
  });

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => robots.setAttribute("content", "index, follow, max-image-preview:large");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payment-links/${slug}`);
        setLink(data);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payment-links/${slug}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'ouvrir Stripe");
      setPaying(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
      </div>
    );

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <XCircle className="h-12 w-12 text-red-400" />
        <h1 className="mt-4 font-display text-2xl font-bold">
          Lien introuvable
        </h1>
        <p className="mt-2 text-sm text-zinc-400 max-w-sm">
          Ce lien de paiement n'existe pas ou a été supprimé. Contactez votre
          interlocuteur ZFIndustries.
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm"
        >
          Retour à l'accueil
        </button>
      </div>
    );

  const paid = link.status === "paid";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-grid">
      <div className="absolute inset-0 bg-radial-violet pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>

        <div
          data-testid="pay-card"
          className="gradient-border rounded-2xl p-7 sm:p-9 shadow-violet-soft"
        >
          {paid ? (
            <div className="text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/40">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold">
                Paiement déjà reçu
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                Ce lien a été utilisé. Merci pour votre confiance.
              </p>
            </div>
          ) : (
            <>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-400 text-center">
                // Paiement sécurisé Stripe
              </p>
              <h1 className="mt-3 text-center font-display text-2xl font-bold">
                Régler votre devis
              </h1>

              <div className="mt-7 rounded-xl border border-violet-500/40 bg-violet-500/5 p-5 text-center">
                <p className="text-xs uppercase tracking-wider text-zinc-500">
                  Montant à payer
                </p>
                <p
                  data-testid="pay-amount"
                  className="mt-1 font-display text-5xl font-extrabold text-white"
                >
                  {link.amount.toFixed(2)}€
                </p>
              </div>

              <div className="mt-5 rounded-xl border border-white/10 bg-zinc-900/50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Description
                </p>
                <p
                  data-testid="pay-description"
                  className="mt-1 text-sm text-zinc-200 leading-relaxed"
                >
                  {link.description}
                </p>
                {link.client_name && (
                  <p className="mt-3 text-xs text-zinc-500">
                    Client : <span className="text-zinc-300">{link.client_name}</span>
                  </p>
                )}
              </div>

              <button
                onClick={pay}
                disabled={paying}
                data-testid="pay-button"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-60 px-6 py-3.5 text-sm font-semibold text-white shadow-violet pulse-violet"
              >
                {paying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Redirection…
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    Payer {link.amount.toFixed(2)}€ par carte
                  </>
                )}
              </button>

              <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-zinc-500">
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="h-3 w-3" /> Paiement chiffré SSL
                </span>
                <span>·</span>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3 w-3" /> Géré par Stripe
                </span>
              </div>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-zinc-600">
          ZFIndustries · Montage PC gaming sur mesure
        </p>
      </div>
    </div>
  );
}
